// `?hairtest=1` — the strand hair renderer (src/lib/hairStrands.js) on a head
// of hair with no character, blueprint, asset manager or Unreal around it.
//
// The question this scene answers comes BEFORE any MetaHuman port: can the
// ribbon renderer reach the look of a strand-rendered groom at all? So the
// geometry is real groom data from a public source (Cem Yuksel's hair models,
// `?hair=wStraight|wCurly`, local files under public/assets/hairtest/) or a
// procedural head (`?hair=procedural`), and every look knob is a URL parameter
// so a headless screenshot is reproducible: `?w=1.5&model=unreal&color=1a0d05
// &rough=0.4&spec=0.35&fill=0.3&az=35&el=40&stride=1&view=front`.
//
// Seam: `window.__HAIR_TEST__` = { state, set(patch), counts, ready, error }.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { HairStrandMaterial, setStrandLook, setStrandResolution, attachStrandDensity, updateStrandDensityFrame, STRAND_MODELS } from '../lib/hairStrands.js'
import { HAIR_TEST_BACKDROP, HAIR_TEST_SCALP, HAIR_TEST_KEY, HAIR_TEST_BACK, HAIR_TEST_SKY, HAIR_TEST_GROUND_BOUNCE } from '../lib/sceneColorDefaults.js'
import { buildHairDensity } from '../lib/hairDensity.js'
import { parseHairFile, hairToSegmentBuffers } from '../lib/hairFormat.js'
import { proceduralHair } from '../lib/proceduralHair.js'

const MODELS = {
  // Yuksel's models are in centimetres, Y up, head near the origin.
  // Yuksel's models hang along -Z (measured on wStraight: mean root-to-tip
  // direction (-0.04, 0.00, -1.00)); the curly one's tips curl back up, so the
  // heuristic below would mis-orient it — the set's convention is declared.
  wStraight: { url: '/assets/hairtest/wStraight.hair', scale: 0.01, down: [0, 0, -1] },
  wCurly: { url: '/assets/hairtest/wCurly.hair', scale: 0.01, down: [0, 0, -1] },
  procedural: { scale: 1, down: [0, -1, 0] },
}

/** Every model is normalised to this scalp radius (a real head is about 9 cm). */
const SCALP_RADIUS_M = 0.09

// Camera views, as (azimuth, elevation) about the root centroid — the same
// four framings the Unreal references were captured at.
const VIEWS = {
  front: [0, 8], threequarter: [40, 10], profile: [90, 5], close: [20, 12],
}

function readLook(params) {
  const num = (k, d) => { const v = parseFloat(params.get(k)); return Number.isFinite(v) ? v : d }
  const hex = params.get('color')
  const color = hex && /^[0-9a-fA-F]{6}$/.test(hex)
    ? [parseInt(hex.slice(0, 2), 16) / 255, parseInt(hex.slice(2, 4), 16) / 255, parseInt(hex.slice(4, 6), 16) / 255].map(c => Math.pow(c, 2.2))
    : null
  const model = params.get('model') in STRAND_MODELS ? params.get('model') : 'unreal'
  return {
    widthPx: num('w', 1.0), diameter: num('diam', 0.0001), coverageScale: num('cov', 1), roughness: num('rough', 0.4), specular: num('spec', 0.15), fill: num('fill', 0.3),
    tilt: num('tilt', 0.15), model, color, azimuth: num('az', 35), elevation: num('el', 40), keyIntensity: num('key', 3),
    backIntensity: num('back', 1), skyIntensity: num('sky', 1.5),
    stride: Math.max(1, Math.floor(num('stride', 1))),
    yaw: num('yaw', 0),
    shadow: num('shadow', 0.8), ao: num('ao', 2.0),
    ui: params.get('ui') !== '0',
  }
}

const panel = {
  position: 'absolute', top: 12, left: 12, zIndex: 10, width: 320,
  padding: '12px 14px', borderRadius: 10, color: 'var(--m-fg)',
  background: 'var(--m-panel)', border: '1px solid var(--m-line)',
  font: '12px/1.5 var(--m-mono)',
}
const row = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }
const label = { width: 84, opacity: 0.75 }

function Slider({ text, value, min, max, step, onChange, fmt = (v) => v.toFixed(2) }) {
  return (
    <div style={row}>
      <span style={label}>{text}</span>
      <input type="range" min={min} max={max} step={step} value={value} style={{ flex: 1 }}
        onChange={(e) => onChange(parseFloat(e.target.value))} />
      <span style={{ width: 48, textAlign: 'right' }}>{fmt(value)}</span>
    </div>
  )
}

/** Builds the ribbons once per (hair, stride); applies the look on every change. */
function Hair({ hair, scale, down: declaredDown, look, onBuilt }) {
  const { gl, camera, controls } = useThree()
  const ribbons = useMemo(() => {
    if (!hair) return null
    const { positions, params, segments, strands, roots } = hairToSegmentBuffers(hair, { stride: look.stride, scale })
    const geometry = new LineSegmentsGeometry()
    geometry.setPositions(positions)
    geometry.setAttribute('instanceParam', new THREE.InstancedBufferAttribute(params, 4))
    const material = new HairStrandMaterial()
    const mesh = new LineSegments2(geometry, material)
    mesh.frustumCulled = false
    mesh.userData.isHairStrands = true
    // Voxel density for self-shadowing and occlusion, in the mesh's own space.
    const density = buildHairDensity(positions, { res: 96 })
    attachStrandDensity(material, density)
    // Head estimate from the roots: their centroid is inside the skull and their
    // mean distance from it is the scalp radius.
    let cx = 0, cy = 0, cz = 0
    for (let i = 0; i < strands; i++) { cx += roots[i * 3]; cy += roots[i * 3 + 1]; cz += roots[i * 3 + 2] }
    cx /= strands; cy /= strands; cz /= strands
    let r = 0
    for (let i = 0; i < strands; i++) r += Math.hypot(roots[i * 3] - cx, roots[i * 3 + 1] - cy, roots[i * 3 + 2] - cz)
    r /= strands
    const box = new THREE.Box3().setFromArray(positions)
    // Orientation is not declared by the file: take the mean root-to-tip direction
    // as "down" and rotate the whole head so it hangs along -Y; `?yaw=` spins it
    // about Y afterwards so the face can be put toward the camera.
    let dx = 0, dy = 0, dz = 0
    for (let i = 0; i < segments; i++) {
      if (params[i * 4 + 1] >= 0.999) { // a strand's last segment: its end is the tip
        dx += positions[i * 6 + 3]; dy += positions[i * 6 + 4]; dz += positions[i * 6 + 5]
      }
    }
    const down = declaredDown
      ? new THREE.Vector3().fromArray(declaredDown)
      : new THREE.Vector3(dx / strands - cx, dy / strands - cy, dz / strands - cz).normalize()
    const q = new THREE.Quaternion().setFromUnitVectors(down, new THREE.Vector3(0, -1, 0))
    q.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(look.yaw)))
    // The file declares no unit (wStraight's scalp comes out at 28 "cm"), so the
    // head is normalised to a real one: scalp radius SCALP_RADIUS_M.
    const k = SCALP_RADIUS_M / r
    const worldCenter = new THREE.Vector3(cx, cy, cz).applyQuaternion(q).multiplyScalar(k)
    const worldBox = box.clone().applyMatrix4(new THREE.Matrix4().compose(new THREE.Vector3(), q, new THREE.Vector3(k, k, k)))
    return {
      mesh, quaternion: q, scale: k, localCenter: [cx, cy, cz],
      head: {
        center: worldCenter.toArray(), radius: SCALP_RADIUS_M, sourceRadius: r,
        // hair bounds after orientation + scale, for framing
        extent: Math.max(...worldBox.getSize(new THREE.Vector3()).toArray()),
        centerY: worldBox.getCenter(new THREE.Vector3()).y,
      },
      counts: { strands, segments, sourceStrands: hair.strandCount, densityVoxels: density.filled },
      box: box.min.toArray().concat(box.max.toArray()), down: down.toArray(),
    }
  }, [hair, scale, declaredDown, look.stride, look.yaw])

  useEffect(() => {
    if (!ribbons) return
    onBuilt(ribbons)
    return () => { ribbons.mesh.geometry.dispose(); ribbons.mesh.material.dispose() }
  }, [ribbons, onBuilt])

  useEffect(() => {
    if (!ribbons) return
    setStrandLook(ribbons.mesh.material, {
      widthPx: look.widthPx, diameter: look.diameter, roughness: look.roughness,
      // the stride thinning stands in for the strands it dropped
      coverageScale: look.coverageScale * look.stride, specular: look.specular, fill: look.fill, tilt: look.tilt, model: look.model,
      shadow: look.shadow, ao: look.ao,
      color: look.color ?? hair.defaultColor.map(c => c * c),
    })
  }, [ribbons, look, hair])

  const strandList = useMemo(() => (ribbons ? [ribbons.mesh] : []), [ribbons])
  useFrame(() => {
    if (!strandList.length) return
    setStrandResolution(strandList, gl)
    updateStrandDensityFrame(ribbons.mesh, camera)
  })

  if (!ribbons) return null
  return (
    <group quaternion={ribbons.quaternion} scale={ribbons.scale}>
      <primitive object={ribbons.mesh} />
      {/* A matte dark head under the hair, so the strands have something to occlude against. */}
      <mesh position={ribbons.localCenter}>
        <sphereGeometry args={[ribbons.head.sourceRadius * 0.985, 48, 32]} />
        {/* Unlit and near-black: a scalp under dark hair, not a lit ball showing through it. */}
        <meshBasicMaterial color={HAIR_TEST_SCALP} />
      </mesh>
    </group>
  )
}

/** Directional key from (azimuth, elevation) about the head; aimed at its centre. */
function KeyLight({ look, head }) {
  const ref = useRef()
  const az = THREE.MathUtils.degToRad(look.azimuth), el = THREE.MathUtils.degToRad(look.elevation)
  const c = head?.center ?? [0, 0, 0]
  const d = 3
  const pos = [c[0] + d * Math.cos(el) * Math.sin(az), c[1] + d * Math.sin(el), c[2] + d * Math.cos(el) * Math.cos(az)]
  useEffect(() => { if (ref.current) { ref.current.target.position.set(c[0], c[1], c[2]); ref.current.target.updateMatrixWorld() } }, [c[0], c[1], c[2]])
  return <directionalLight ref={ref} position={pos} intensity={look.keyIntensity} color={HAIR_TEST_KEY} />
}

/** A cool back light from behind and above the head (the sky side), for rim and transmission. */
function BackLight({ look, head }) {
  const ref = useRef()
  const c = head?.center ?? [0, 0, 0]
  const az = THREE.MathUtils.degToRad(look.azimuth + 160), el = THREE.MathUtils.degToRad(55)
  const d = 3
  const pos = [c[0] + d * Math.cos(el) * Math.sin(az), c[1] + d * Math.sin(el), c[2] + d * Math.cos(el) * Math.cos(az)]
  useEffect(() => { if (ref.current) { ref.current.target.position.set(c[0], c[1], c[2]); ref.current.target.updateMatrixWorld() } }, [c[0], c[1], c[2]])
  if (look.backIntensity <= 0) return null
  return <directionalLight ref={ref} position={pos} intensity={look.backIntensity} color={HAIR_TEST_BACK} />
}

/** Puts the camera at the named view once the head is known; OrbitControls takes over after. */
function ViewFraming({ head, view, controlsRef }) {
  const { camera } = useThree()
  const controls = useThree((s) => s.controls)
  useEffect(() => {
    if (!head || !controls) return
    const [azDeg, elDeg] = VIEWS[view] ?? VIEWS.front
    const az = THREE.MathUtils.degToRad(azDeg), el = THREE.MathUtils.degToRad(elDeg)
    // Frame the hair's own bounds (a wig hangs far below the scalp), the close
    // view the crown.
    const [cx, cy, cz] = head.center
    const hairHeight = head.extent
    const dist = view === 'close' ? head.radius * 4.5 : hairHeight * 1.9
    const ty = view === 'close' ? cy - head.radius * 0.1 : head.centerY
    camera.position.set(cx + dist * Math.cos(el) * Math.sin(az), ty + dist * Math.sin(el), cz + dist * Math.cos(el) * Math.cos(az))
    camera.near = 0.01; camera.far = 50; camera.updateProjectionMatrix()
    if (controlsRef.current) { controlsRef.current.target.set(cx, ty, cz); controlsRef.current.update() }
    else camera.lookAt(cx, ty, cz)
  }, [head, view, camera, controlsRef, controls])
  return null
}

export default function HairTestApp() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const modelKey = MODELS[params.get('hair')] ? params.get('hair') : 'wStraight'
  const view = params.get('view') ?? 'front'
  const [look, setLook] = useState(() => readLook(params))
  const [hair, setHair] = useState(null)
  const [error, setError] = useState(null)
  const [built, setBuilt] = useState(null)
  const controlsRef = useRef()

  useEffect(() => {
    let cancelled = false
    const spec = MODELS[modelKey]
    ;(async () => {
      try {
        if (modelKey === 'procedural') {
          setHair(proceduralHair({ strands: 40000, curl: parseFloat(params.get('curl') ?? '0.15') }))
          return
        }
        const res = await fetch(spec.url)
        if (!res.ok) throw new Error(`${spec.url}: HTTP ${res.status} — the model is a local-only file, see .gitignore`)
        const parsed = parseHairFile(await res.arrayBuffer())
        if (!cancelled) setHair(parsed)
      } catch (e) {
        if (!cancelled) setError(String(e?.message ?? e))
      }
    })()
    return () => { cancelled = true }
  }, [modelKey, params])

  useEffect(() => {
    window.__HAIR_TEST__ = {
      get state() { return { model: modelKey, view, look, ready: !!built, error } },
      get counts() { return built?.counts ?? null },
      get head() { return built?.head ?? null },
      get box() { return built?.box ?? null },
      get down() { return built?.down ?? null },
      get ready() { return !!built },
      get error() { return error },
      set: (patch) => setLook(prev => ({ ...prev, ...patch })),
      material: () => built?.mesh.material ?? null,
    }
    return () => { delete window.__HAIR_TEST__ }
  }, [modelKey, view, look, built, error])

  const set = (k) => (v) => setLook(prev => ({ ...prev, [k]: v }))
  const hex = (look.color ?? hair?.defaultColor?.map(c => c * c) ?? [0, 0, 0]).map(c => Math.round(Math.pow(c, 1 / 2.2) * 255).toString(16).padStart(2, '0')).join('')

  return (
    <div style={{ position: 'fixed', inset: 0, background: HAIR_TEST_BACKDROP }}>
      <div style={{ ...panel, display: look.ui ? 'block' : 'none' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>HAIR TEST — {modelKey}</div>
        {error && <div style={{ color: 'var(--m-danger)', whiteSpace: 'pre-wrap' }}>{error}</div>}
        {!error && !built && <div style={{ opacity: 0.8 }}>loading…</div>}
        {built && (
          <div style={{ opacity: 0.75 }}>
            {built.counts.strands.toLocaleString()} of {built.counts.sourceStrands.toLocaleString()} strands, {built.counts.segments.toLocaleString()} segments
          </div>
        )}
        <div style={row}>
          <span style={label}>model</span>
          {Object.keys(STRAND_MODELS).map(m => (
            <button key={m} onClick={() => set('model')(m)} style={{ opacity: look.model === m ? 1 : 0.45 }}>{m}</button>
          ))}
        </div>
        <div style={row}>
          <span style={label}>hair</span>
          {Object.keys(MODELS).map(m => (
            <a key={m} href={`?hairtest=1&hair=${m}&view=${view}`} style={{ opacity: modelKey === m ? 1 : 0.5, color: 'inherit' }}>{m}</a>
          ))}
        </div>
        <Slider text="min px" value={look.widthPx} min={0.5} max={4} step={0.1} onChange={set('widthPx')} />
        <Slider text="coverage x" value={look.coverageScale} min={0.5} max={4} step={0.1} onChange={set('coverageScale')} fmt={v => v.toFixed(1)} />
        <Slider text="diam µm" value={look.diameter * 1e6} min={20} max={400} step={5} onChange={(v) => set('diameter')(v * 1e-6)} fmt={v => v.toFixed(0)} />
        <Slider text="stride" value={look.stride} min={1} max={8} step={1} onChange={set('stride')} fmt={v => `1/${v}`} />
        <Slider text="roughness" value={look.roughness} min={0} max={1} step={0.01} onChange={set('roughness')} />
        <Slider text="specular" value={look.specular} min={0} max={2} step={0.01} onChange={set('specular')} />
        <Slider text="fill" value={look.fill} min={0} max={1} step={0.01} onChange={set('fill')} />
        <Slider text="tilt" value={look.tilt} min={0} max={0.6} step={0.01} onChange={set('tilt')} />
        <Slider text="key" value={look.keyIntensity} min={0} max={8} step={0.1} onChange={set('keyIntensity')} />
        <Slider text="sky" value={look.skyIntensity} min={0} max={5} step={0.1} onChange={set('skyIntensity')} />
        <Slider text="back" value={look.backIntensity} min={0} max={6} step={0.1} onChange={set('backIntensity')} />
        <Slider text="light az" value={look.azimuth} min={-180} max={180} step={1} onChange={set('azimuth')} fmt={v => `${v.toFixed(0)}°`} />
        <Slider text="shadow" value={look.shadow} min={0} max={3} step={0.05} onChange={set('shadow')} />
        <Slider text="ao" value={look.ao} min={0} max={4} step={0.05} onChange={set('ao')} />
        <Slider text="yaw" value={look.yaw} min={-180} max={180} step={1} onChange={set('yaw')} fmt={v => `${v.toFixed(0)}°`} />
        <Slider text="light el" value={look.elevation} min={-10} max={85} step={1} onChange={set('elevation')} fmt={v => `${v.toFixed(0)}°`} />
        <div style={row}>
          <span style={label}>color</span>
          <input type="color" value={`#${hex}`} onChange={(e) => {
            const h = e.target.value.slice(1)
            set('color')([0, 2, 4].map(i => Math.pow(parseInt(h.slice(i, i + 2), 16) / 255, 2.2)))
          }} />
          <span style={{ opacity: 0.6 }}>#{hex}</span>
        </div>
      </div>
      <Canvas camera={{ position: [0, 0, 1], fov: 30, near: 0.01, far: 50 }} dpr={[1, 2]} gl={{ antialias: true }}>
        <color attach="background" args={[HAIR_TEST_BACKDROP]} />
        <ambientLight intensity={0.15} />
        {/* A sky dome: the reference is lit by one, and dark hair with no sky crushes to black. */}
        {look.skyIntensity > 0 && <hemisphereLight args={[HAIR_TEST_SKY, HAIR_TEST_GROUND_BOUNCE, look.skyIntensity]} />}
        <KeyLight look={look} head={built?.head} />
        <BackLight look={look} head={built?.head} />
        <Hair hair={hair} scale={MODELS[modelKey].scale} down={MODELS[modelKey].down} look={look} onBuilt={setBuilt} />
        <ViewFraming head={built?.head} view={view} controlsRef={controlsRef} />
        <OrbitControls ref={controlsRef} makeDefault />
      </Canvas>
    </div>
  )
}
